<template>
  <div class="question-display-root">
    <!-- Question header: number, text, subtags/tag, score -->
    <div class="question">
      <!-- Edit/delete buttons (only in edit mode) -->
      <b-row v-if="!readOnly" class="float-right">
        <slot name="edit-actions"></slot>
      </b-row>

      <b-row class="w-100">
        <b-col v-if="!bare" class="pl-0" cols="1">
          <div class="qd-circle">
            <p>{{ displayIndex }}</p>
          </div>
        </b-col>
        <b-col class="pl-0" :cols="bare ? 12 : 11">
          <strong>
            <p style="white-space: pre-wrap">
              {{ question.question }}
            </p>
          </strong>
        </b-col>
      </b-row>

      <!-- Subtags -->
      <b-row v-if="!bare && question.subtags && question.subtags.length > 0">
        <div
          v-for="(subtag, subtagIndex) in question.subtags"
          :key="`subtag-q${questionIndex}-${subtag.id}-${subtagIndex}`"
          class="d-block"
        >
          <b-badge variant="primary">
            {{ subtag.parent_name + " - " + subtag.parent_summary }}
            <br />
            {{ "IND: " + subtag.summary }}
          </b-badge>
        </div>
      </b-row>
      <b-row v-else-if="!bare && question.tag">
        <div class="d-block">
          <b-badge variant="primary">
            {{ question.tag.name + " - " + question.tag.summary }}
          </b-badge>
        </div>
      </b-row>

      <!-- Score -->
      <b-row v-if="readOnly && question.score && !bare">
        <b-col cols="2" class="text-right mr-2 pr-0">
          <strong>Puntaje:</strong>
        </b-col>
        <b-col cols="2" class="text-left ml-0 pl-0">
          {{ question.score }}
        </b-col>
      </b-row>
      <b-row v-if="!readOnly">
        <b-col cols="2" class="text-right mr-2 pr-0">
          <strong>Puntaje:</strong>
        </b-col>
        <b-col cols="2" class="text-left ml-0 pl-0">
          <b-input type="number" v-model="question.score" />
        </b-col>
      </b-row>

      <!-- Formulas (not for grid/matrix/search/visual key kinds) -->
      <div v-if="question.formulas && question.kind !== 6 && question.kind !== 7 && question.kind !== 8 && question.kind !== 9 && question.kind !== 10">
        <b-form-row
          v-for="(formula, findex) in question.formulas"
          :key="`formula-${question.id || 'temp'}-${findex}`"
          class="px-3"
        >
          <b-col>
            <math-field
              id="formula"
              ref="formula"
              style="height: 50px; font-size: 20pt"
              readonly="true"
            >
              {{ formula }}
            </math-field>
          </b-col>
        </b-form-row>
      </div>

      <!-- Question audio -->
      <div v-if="question.audio" class="d-inline-block">
        <audio
          :ref="`player-${playerPrefix}-question-${questionIndex}`"
          :src="question.audio"
          @ended="() => { $emit('toggle-player', ''); }"
        ></audio>
        <div v-if="nowPlaying !== `${playerPrefix}-question-${questionIndex}`">
          <b-img
            @click="$emit('toggle-player', `${playerPrefix}-question-${questionIndex}`)"
            width="30px"
            style="cursor: pointer"
            :src="listenIcon"
          />
        </div>
        <div v-else>
          <b-img
            @click="$emit('toggle-player', `${playerPrefix}-question-${questionIndex}`)"
            width="30px"
            style="cursor: pointer"
            :src="repeatIcon"
          />
          <b-img
            @click="handlePause(`${playerPrefix}-question-${questionIndex}`)"
            width="30px"
            style="cursor: pointer"
            :src="isPaused(`${playerPrefix}-question-${questionIndex}`) ? resumeIcon : pauseIcon"
          />
        </div>
      </div>

      <!-- Question image -->
      <b-row v-if="question.image">
        <b-col class="text-center">
          <b-img :src="question.image" width="320px"></b-img>
        </b-col>
      </b-row>
    </div>

    <!-- Kind 0: Alternatives -->
    <div v-if="question.kind === 0" class="question">
      <div class="mb-3" v-if="question.is_count_question">
        <CountQuestionComponent :question="question" />
      </div>
      <b-form-row
        v-for="(alternative, aindex) of question.alternatives"
        :key="`alt-${question.id || 'temp'}-${aindex}`"
        class="alternative"
        :class="{
          alt_correct: alternative.is_correct,
          alt_incorrect: !alternative.is_correct,
        }"
      >
        <b-col cols="12" v-if="alternative.text">
          <p v-if="!alternative.is_formula && alternative.text">
            {{ alternative.text }}
          </p>
          <math-field
            class="align-middle"
            v-if="alternative.is_formula"
            :key="alternative.text"
            style="pointer-events: none; height: 50px; font-size: 20pt; margin-bottom: 10px;"
            id="formula"
            :read-only="true"
          >
            {{ alternative.text }}
          </math-field>
        </b-col>
        <b-col cols="12" v-if="alternative.audio" class="text-left py-1">
          <audio
            :ref="`player-${playerPrefix}-question-${questionIndex}-alt-${aindex}`"
            :src="alternative.audio"
            @ended="() => { $emit('toggle-player', ''); }"
          ></audio>
          <div v-if="nowPlaying !== `${playerPrefix}-question-${questionIndex}-alt-${aindex}`">
            <b-img
              @click="$emit('toggle-player', `${playerPrefix}-question-${questionIndex}-alt-${aindex}`)"
              width="25px"
              style="cursor: pointer"
              :src="listenIcon"
            />
          </div>
          <div v-else>
            <b-img
              @click="$emit('toggle-player', `${playerPrefix}-question-${questionIndex}-alt-${aindex}`)"
              width="25px"
              style="cursor: pointer"
              :src="repeatIcon"
            />
            <b-img
              @click="handlePause(`${playerPrefix}-question-${questionIndex}-alt-${aindex}`)"
              width="25px"
              style="cursor: pointer"
              :src="isPaused(`${playerPrefix}-question-${questionIndex}-alt-${aindex}`) ? resumeIcon : pauseIcon"
            />
          </div>
        </b-col>
        <b-col cols="12" v-if="alternative.image" class="text-left py-1">
          <b-img :src="alternative.image" width="320px" />
        </b-col>
      </b-form-row>
    </div>

    <!-- Kind 1: Essay -->
    <div v-if="question.kind === 1" class="question">
      <b-row class="essay_question">
        <p v-if="question.response_type === 1">Escribe una respuesta ...</p>
        <p v-else-if="question.response_type === 2">Adjunta una imagen para responder ...</p>
        <p v-else-if="question.response_type === 3">Graba un audio con tu respuesta ...</p>
        <p v-else-if="question.response_type === 4">Responde con una carta ...</p>
        <p v-else>Responde con una noticia ...</p>
      </b-row>
    </div>

    <!-- Kind 2: Dictation -->
    <div v-if="question.kind === 2" class="question">
      <b-row class="dictation_question">
        <p>Ingresa lo dictado en los {{ question.dictation }} audios ...</p>
      </b-row>
    </div>

    <!-- Kind 3: Correct/Incorrect Options -->
    <div v-if="question.kind === 3" class="question">
      <b-row>
        <b-col cols="5" class="options">
          <div
            v-for="(option, oindex) of question.options"
            :key="`opt-correct-${question.id || 'temp'}-${oindex}`"
          >
            <b-row v-if="option.correct" class="correct-option">
              <b-col v-if="option.text" cols="12">
                <p>{{ option.text }}</p>
              </b-col>
              <b-col v-if="option.audio" cols="12" class="text-left">
                <audio
                  :ref="`player-${playerPrefix}-question-${questionIndex}-opt-${oindex}`"
                  :src="option.audio"
                  @ended="() => { $emit('toggle-player', ''); }"
                ></audio>
                <div v-if="nowPlaying !== `${playerPrefix}-question-${questionIndex}-opt-${oindex}`">
                  <b-img
                    @click="$emit('toggle-player', `${playerPrefix}-question-${questionIndex}-opt-${oindex}`)"
                    width="25px"
                    style="cursor: pointer"
                    :src="listenIcon"
                  />
                </div>
                <div v-else>
                  <b-img
                    @click="$emit('toggle-player', `${playerPrefix}-question-${questionIndex}-opt-${oindex}`)"
                    width="25px"
                    style="cursor: pointer"
                    :src="repeatIcon"
                  />
                  <b-img
                    @click="handlePause(`${playerPrefix}-question-${questionIndex}-opt-${oindex}`)"
                    width="25px"
                    style="cursor: pointer"
                    :src="isPaused(`${playerPrefix}-question-${questionIndex}-opt-${oindex}`) ? resumeIcon : pauseIcon"
                  />
                </div>
              </b-col>
              <b-col v-if="option.image" cols="12" class="text-left">
                <b-img :src="option.image" class="w-100" />
              </b-col>
            </b-row>
          </div>
        </b-col>
        <b-col cols="5" class="options">
          <div
            v-for="(option, oindex) of question.options"
            :key="`opt-incorrect-${question.id || 'temp'}-${oindex}`"
          >
            <b-row v-if="!option.correct" class="incorrect-option">
              <b-col v-if="option.text" cols="12">
                <p>{{ option.text }}</p>
              </b-col>
              <b-col v-if="option.audio" cols="12" class="text-left">
                <audio
                  :ref="`player-${playerPrefix}-question-${questionIndex}-opt-${oindex}`"
                  :src="option.audio"
                  @ended="() => { $emit('toggle-player', ''); }"
                ></audio>
                <div v-if="nowPlaying !== `${playerPrefix}-question-${questionIndex}-opt-${oindex}`">
                  <b-img
                    @click="$emit('toggle-player', `${playerPrefix}-question-${questionIndex}-opt-${oindex}`)"
                    width="25px"
                    style="cursor: pointer"
                    :src="listenIcon"
                  />
                </div>
                <div v-else>
                  <b-img
                    @click="$emit('toggle-player', `${playerPrefix}-question-${questionIndex}-opt-${oindex}`)"
                    width="25px"
                    style="cursor: pointer"
                    :src="repeatIcon"
                  />
                  <b-img
                    @click="handlePause(`${playerPrefix}-question-${questionIndex}-opt-${oindex}`)"
                    width="25px"
                    style="cursor: pointer"
                    :src="isPaused(`${playerPrefix}-question-${questionIndex}-opt-${oindex}`) ? resumeIcon : pauseIcon"
                  />
                </div>
              </b-col>
              <b-col v-if="option.image" cols="12" class="text-left">
                <b-img :src="option.image" class="w-100" />
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Kind 5: Puzzle -->
    <div v-if="question.kind === 5" class="question puzzle-question">
      <div class="puzzle-options-row">
        <div
          v-for="(option, oindex) of question.options"
          :key="`puzzle-opt-${question.id || 'temp'}-${oindex}`"
          class="puzzle-option-item"
          :class="option.correct ? 'puzzle-correct-option' : 'puzzle-incorrect-option'"
        >
          <div class="puzzle-option-content">
            <div v-if="option.text" class="puzzle-text">
              <p>{{ option.text }}</p>
            </div>
            <div v-if="option.audio" class="puzzle-audio">
              <audio
                :ref="`player-${playerPrefix}-question-${questionIndex}-opt-${oindex}`"
                :src="option.audio"
                @ended="() => { $emit('toggle-player', ''); }"
              ></audio>
              <div v-if="nowPlaying !== `${playerPrefix}-question-${questionIndex}-opt-${oindex}`">
                <b-img
                  @click="$emit('toggle-player', `${playerPrefix}-question-${questionIndex}-opt-${oindex}`)"
                  width="25px"
                  style="cursor: pointer"
                  :src="listenIcon"
                />
              </div>
              <div v-else>
                <b-img
                  @click="$emit('toggle-player', `${playerPrefix}-question-${questionIndex}-opt-${oindex}`)"
                  width="25px"
                  style="cursor: pointer"
                  :src="repeatIcon"
                />
                <b-img
                  @click="handlePause(`${playerPrefix}-question-${questionIndex}-opt-${oindex}`)"
                  width="25px"
                  style="cursor: pointer"
                  :src="isPaused(`${playerPrefix}-question-${questionIndex}-opt-${oindex}`) ? resumeIcon : pauseIcon"
                />
              </div>
            </div>
            <div v-if="option.image" class="puzzle-image">
              <b-img :src="option.image" class="w-100" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Kind 6: Grid -->
    <div v-if="question.kind === 6" class="question grid-question">
      <div class="grid-preview" :style="gridPreviewStyle(question)">
        <div
          v-for="(cell, idx) in gridPreviewCells(question)"
          :key="`gridcell-`+idx"
          class="grid-preview-cell"
          :style="gridPieceBackground(cell)"
        >
          <span v-if="!cell">-</span>
          <span v-else>{{ cell }}</span>
        </div>
      </div>
    </div>

    <!-- Kind 7: Matrix -->
    <div v-if="question.kind === 7" class="question matrix-question">
      <div class="matrix-preview" :style="matrixPreviewStyle(question)">
        <div
          v-for="(cell, idx) in matrixPreviewCells(question)"
          :key="`matrixcell-`+idx"
          class="matrix-preview-cell"
          :class="{ 'question-mark-cell': idx === matrixQuestionPosition(question) - 1 }"
        >
          <div v-if="idx !== matrixQuestionPosition(question) - 1 && cell.image" class="matrix-image-container">
            <b-img :src="cell.image" fluid />
          </div>
          <div v-else-if="idx === matrixQuestionPosition(question) - 1" class="question-mark">
            ?
          </div>
          <div v-else class="matrix-placeholder">
            Imagen {{ idx + 1 }}
          </div>
        </div>
      </div>
      <div class="answer-options-preview">
        <div
          v-for="(option, idx) in question.options"
          :key="`answer-option-`+idx"
          class="answer-option-preview"
          :class="{ 'correct-answer': option.correct }"
        >
          <div v-if="option.image" class="answer-image-container">
            <b-img
              :src="option.image.file ? option.image_url : option.image"
              fluid
            />
          </div>
          <div v-else class="answer-placeholder">
            Op {{ idx + 1 }}
          </div>
          <div class="answer-number">{{ idx + 1 }}</div>
        </div>
      </div>
    </div>

    <!-- Kind 8 & 10: Search -->
    <div v-if="question.kind === 8 || question.kind === 10" class="question search-question">
      <div class="search-instruction">
        <p><strong>Busca en la cuadrícula los siguientes símbolos:</strong></p>
        <div class="goal-symbols-display" v-if="searchGoalSymbols(question).length > 0">
          <span class="goal-symbol" v-for="symbol in searchGoalSymbols(question)" :key="symbol">
            {{ symbol }}
          </span>
        </div>
      </div>
      <div class="search-grid-preview" :style="searchGridPreviewStyle(question)">
        <div
          v-for="(cell, idx) in searchGridPreviewCells(question)"
          :key="`searchcell-`+idx"
          class="search-grid-preview-cell"
        >
          {{ cell }}
        </div>
      </div>
    </div>

    <!-- Kind 9: Visual Key -->
    <div v-if="question.kind === 9" class="question vk-preview">
      <div class="vk-preview-keys-container">
        <div class="vk-preview-keys-title">🔑 Tabla de Claves</div>
        <div class="vk-preview-keys">
          <div v-for="k in visualKeySymbols()" :key="'pv-k-'+k.n" class="vk-preview-key">
            <div class="vk-preview-key-number">{{ k.n }}</div>
            <div class="vk-preview-key-symbol">{{ k.s }}</div>
          </div>
        </div>
      </div>
      <div class="vk-preview-grid-container">
        <div class="vk-preview-grid-title">✏️ Completa dibujando el símbolo correspondiente</div>
        <div class="vk-preview-grid" :style="visualKeyPreviewGridStyle(question)">
          <template v-for="(row, rIdx) in visualKeyPreviewGrid(question)">
            <div v-for="(num, cIdx) in row" :key="`pv-vkcell-${rIdx}-${cIdx}`" class="vk-preview-cell">
              <div class="vk-preview-number">{{ num }}</div>
              <div class="vk-preview-draw"></div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Kind 11: Writing -->
    <div v-if="question.kind === 11" class="question writing-preview">
      <div v-if="question.writing_config && question.writing_config.exercise_type === 'traces'" class="traces-preview">
        <div class="writing-preview-title">✏️ Ejercicios de Trazos</div>
        <div v-for="(trace, tIndex) in question.writing_config.traces" :key="`trace-${tIndex}`" class="trace-preview-box">
          <div class="trace-preview-content">
            <div class="trace-icon" :style="{ fontSize: getTraceIconSize(question) + 'px' }">
              {{ trace.startIcon }}
            </div>
            <div class="trace-svg-container">
              <div class="trace-svg" v-html="generateTraceSVG(trace, question.writing_config.traces_visual)"></div>
            </div>
            <div class="trace-icon" :style="{ fontSize: getTraceIconSize(question) + 'px' }">
              {{ trace.endIcon }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="question.writing_config && question.writing_config.exercise_type === 'caligraphy'" class="caligraphy-preview">
        <div class="writing-preview-title">✍️ Ejercicios de Caligrafía</div>
        <div v-for="(item, cIndex) in question.writing_config.caligraphy.content" :key="`caligraphy-${cIndex}`" class="caligraphy-exercise-preview">
          <div class="exercise-header-preview">{{ cIndex + 1 }}. Practica: "{{ item }}"</div>
          <div class="exercise-steps-preview">
            <div class="step-box-preview step-model-preview">
              <div class="step-label-preview">MODELO</div>
              <div v-if="getCaligraphyShowGuides(question)" class="guide-line-preview"></div>
              <div class="text-model-preview" :style="{ fontSize: getCaligraphyFontSize(question) + 'px' }">{{ item }}</div>
              <div v-if="getCaligraphyShowGuides(question)" class="guide-line-preview"></div>
            </div>
            <div class="step-box-preview step-trace-preview">
              <div class="step-label-preview">PUNTEADO</div>
              <div v-if="getCaligraphyShowGuides(question)" class="guide-line-preview"></div>
              <div class="text-dotted-preview" :style="{ fontSize: getCaligraphyFontSize(question) + 'px' }">{{ item }}</div>
              <div v-if="getCaligraphyShowGuides(question)" class="guide-line-preview"></div>
            </div>
            <div class="step-box-preview step-practice-preview">
              <div class="step-label-preview">PRÁCTICA</div>
              <div v-if="getCaligraphyShowGuides(question)" class="guide-line-preview"></div>
              <div class="practice-space-preview" :style="{ height: getCaligraphyFontSize(question) + 10 + 'px' }"></div>
              <div v-if="getCaligraphyShowGuides(question)" class="guide-line-preview"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Kind 12: Reading Evaluation -->
    <div v-if="question.kind === 12 && question.reading_evaluation_data && question.reading_evaluation_data.phrases && question.reading_evaluation_data.phrases.length > 0" class="question reading-evaluation-display">
      <div class="reading-evaluation-table-container">
        <div class="table-responsive">
          <table class="reading-evaluation-table">
            <thead>
              <tr class="table-header-gradient">
                <th class="text-left">N°</th>
                <th class="text-left">Frase</th>
                <th class="text-center">Palabras en la frase</th>
                <th class="text-center">Palabras acumuladas</th>
                <th class="text-center">Puntuación (.,;)</th>
                <th class="text-center">Expresión (!¡¿?)</th>
                <th class="text-center">Decodificación (errores)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="phrase in question.reading_evaluation_data.phrases" :key="phrase.numero">
                <td class="numeric-value">{{ phrase.numero }}</td>
                <td class="phrase-text">{{ phrase.frase }}</td>
                <td class="text-center numeric-value">{{ phrase.palabrasSinPuntuacion }}</td>
                <td class="text-center numeric-value">{{ phrase.acumuladoSinPuntuacion }}</td>
                <td class="text-center numeric-value">{{ phrase.puntuacion }}</td>
                <td class="text-center numeric-value">{{ phrase.expresion }}</td>
                <td class="text-center">
                  <span class="decodificacion-display">{{ phrase.decodificacion || 0 }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CountQuestionComponent from "./CountQuestionComponent.vue";

export default {
  name: "QuestionDisplay",
  components: {
    CountQuestionComponent,
  },
  props: {
    question: {
      type: Object,
      required: true,
    },
    questionIndex: {
      type: Number,
      default: 0,
    },
    pageIndex: {
      type: Number,
      default: 0,
    },
    nowPlaying: {
      type: String,
      default: "",
    },
    readOnly: {
      type: Boolean,
      default: true,
    },
    displayIndex: {
      type: [Number, String],
      default: 1,
    },
    // Sin número, etiquetas de indicador ni puntaje: para quien ya los
    // muestra en su propio encabezado (el editor de versiones de pruebas
    // informales).
    bare: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      listenIcon: require("../../../assets/img/eureka_icons/icono_escuchar.svg"),
      repeatIcon: require("../../../assets/img/eureka_icons/icono_repetiraudio.svg"),
      resumeIcon: require("../../../assets/img/eureka_icons/icono_comenzar.svg"),
      pauseIcon: require("../../../assets/img/eureka_icons/icono_reproduciendo.svg"),
    };
  },
  computed: {
    playerPrefix() {
      return `page-${this.pageIndex}`;
    },
  },
  methods: {
    isPaused(name) {
      const ref = this.$refs[`player-${name}`];
      return ref && ref[0] && ref[0].paused;
    },
    handlePause(name) {
      const ref = this.$refs[`player-${name}`];
      if (!ref || !ref[0]) return;
      if (ref[0].paused) {
        ref[0].play();
      } else {
        ref[0].pause();
      }
    },

    // Grid helpers (kind 6)
    gridPreviewStyle(question) {
      try {
        let rows = question.grid_rows;
        let cols = question.grid_cols;
        if ((!rows || !cols) && question.formulas && question.formulas.length) {
          const parsed = JSON.parse(question.formulas[0]);
          rows = parsed.rows;
          cols = parsed.cols;
        }
        if (!rows || !cols) return {};
        return {
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, 60px)`,
          gridTemplateRows: `repeat(${rows}, 60px)`,
          gap: "6px",
        };
      } catch (e) {
        return {};
      }
    },
    gridPreviewCells(question) {
      try {
        if (question.grid_layout && question.grid_layout.length) {
          return question.grid_layout;
        }
        if (question.formulas && question.formulas.length) {
          const parsed = JSON.parse(question.formulas[0]);
          return parsed.layout || [];
        }
      } catch (e) {
        /* ignore */
      }
      return [];
    },
    gridPieceBackground(n) {
      if (!n) return {};
      const yellow = "#f8bf3b";
      const teal = "#08a6b2";
      if (n === 1) return { background: yellow };
      if (n === 2) return { background: teal };
      if (n === 3) {
        return {
          backgroundImage: `linear-gradient(135deg, ${yellow} 50%, transparent 50%), linear-gradient(135deg, transparent 50%, ${teal} 50%)`,
        };
      }
      if (n === 4) {
        return {
          backgroundImage: `linear-gradient(135deg, ${teal} 50%, transparent 50%), linear-gradient(135deg, transparent 50%, ${yellow} 50%)`,
        };
      }
      if (n === 5) {
        return {
          backgroundImage: `linear-gradient(45deg, ${yellow} 50%, transparent 50%), linear-gradient(45deg, transparent 50%, ${teal} 50%)`,
        };
      }
      return {
        backgroundImage: `linear-gradient(45deg, ${teal} 50%, transparent 50%), linear-gradient(45deg, transparent 50%, ${yellow} 50%)`,
      };
    },

    // Matrix helpers (kind 7)
    matrixPreviewStyle(question) {
      try {
        let layoutType = question.matrix_layout_type;
        if (!layoutType && question.formulas && question.formulas.length) {
          const parsed = JSON.parse(question.formulas[0]);
          layoutType = parsed.layout_type;
        }
        if (!layoutType) return {};
        if (layoutType === "2x2") {
          return { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gridTemplateRows: "repeat(2, 1fr)", gap: "8px", maxWidth: "400px" };
        } else if (layoutType === "1x4") {
          return { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gridTemplateRows: "1fr", gap: "8px", maxWidth: "600px" };
        } else if (layoutType === "1x6") {
          return { display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gridTemplateRows: "1fr", gap: "8px", maxWidth: "800px" };
        }
        return {};
      } catch (e) {
        return {};
      }
    },
    matrixPreviewCells(question) {
      try {
        let cells = [];
        if (question.matrix_cells && question.matrix_cells.length) {
          cells = question.matrix_cells;
        } else if (question.formulas && question.formulas.length) {
          const parsed = JSON.parse(question.formulas[0]);
          cells = parsed.cells || [];
        }
        if (question.set_of_images && question.set_of_images.length > 0) {
          const questionIndex = (this.matrixQuestionPosition(question) || 1) - 1;
          let imageIndex = 0;
          cells = cells.map((cell, idx) => {
            const resultCell = { ...cell };
            if (idx !== questionIndex && imageIndex < question.set_of_images.length) {
              resultCell.image = question.set_of_images[imageIndex];
              imageIndex += 1;
            } else if (idx === questionIndex) {
              if (resultCell && resultCell.image) {
                delete resultCell.image;
              }
            }
            return resultCell;
          });
        }
        return cells;
      } catch (e) {
        return [];
      }
    },
    matrixQuestionPosition(question) {
      try {
        let position = question.matrix_question_position;
        if (!position && question.formulas && question.formulas.length) {
          const parsed = JSON.parse(question.formulas[0]);
          position = parsed.question_position;
        }
        return position || 6;
      } catch (e) {
        return 6;
      }
    },

    // Search helpers (kind 8 & 10)
    searchGoalSymbols(question) {
      try {
        if (question.formulas && question.formulas.length) {
          const parsed = JSON.parse(question.formulas[0]);
          return parsed.goal_symbols || [];
        }
        return [];
      } catch (e) {
        return [];
      }
    },
    searchGridPreviewStyle(question) {
      try {
        let gridSize = 5;
        if (question.formulas && question.formulas.length) {
          const parsed = JSON.parse(question.formulas[0]);
          gridSize = parsed.grid_size || 5;
        }
        return {
          display: "grid",
          gridTemplateColumns: `repeat(${gridSize}, 30px)`,
          gridTemplateRows: `repeat(${gridSize}, 30px)`,
          gap: "2px",
          border: "2px solid #ccc",
          padding: "5px",
          backgroundColor: "#f9f9f9",
        };
      } catch (e) {
        return {};
      }
    },
    searchGridPreviewCells(question) {
      try {
        let gridLayout = [];
        if (question.formulas && question.formulas.length) {
          const parsed = JSON.parse(question.formulas[0]);
          gridLayout = parsed.grid_layout || [];
        }
        if (gridLayout && gridLayout.length > 0) {
          return gridLayout;
        }
        let gridSize = 5;
        if (question.formulas && question.formulas.length) {
          const parsed = JSON.parse(question.formulas[0]);
          gridSize = parsed.grid_size || 5;
        }
        const total = gridSize * gridSize;
        return Array.from({ length: total }, () => "?");
      } catch (e) {
        return [];
      }
    },

    // Visual Key helpers (kind 9)
    visualKeySymbols() {
      return [
        { n: 1, s: "/" },
        { n: 2, s: "−" },
        { n: 3, s: "△" },
        { n: 4, s: "ⅹ" },
        { n: 5, s: "\\" },
        { n: 6, s: "○" },
        { n: 7, s: "│" },
        { n: 8, s: "+" },
        { n: 9, s: "□" },
      ];
    },
    visualKeyPreviewGridStyle(question) {
      try {
        let rows = question.visual_key_rows || 3;
        if (!rows && question.formulas && question.formulas.length) {
          const parsed = JSON.parse(question.formulas[0]);
          rows = parsed.rows || 3;
        }
        return { display: "grid", gridTemplateColumns: "repeat(9, 1fr)", gap: "8px" };
      } catch (e) {
        return { display: "grid", gridTemplateColumns: "repeat(9, 1fr)", gap: "8px" };
      }
    },
    visualKeyPreviewGrid(question) {
      try {
        if (question.visual_key_grid && question.visual_key_grid.length) {
          return question.visual_key_grid;
        }
        if (question.formulas && question.formulas.length) {
          const parsed = JSON.parse(question.formulas[0]);
          if (Array.isArray(parsed.grid)) return parsed.grid;
        }
      } catch (e) {
        /* ignore */
      }
      const rows = question.visual_key_rows || 3;
      return Array.from({ length: rows }, () => Array.from({ length: 9 }, () => ""));
    },

    // Writing helpers (kind 11)
    generateTraceSVG(trace, visualConfig) {
      if (!trace || !visualConfig) return "";
      const width = 400;
      const height = visualConfig.boxHeight || 120;
      const cy = height / 2;
      const A = Math.min(40, height * 0.35);
      const innerStroke = visualConfig.lineWidth || 10;
      const outerStroke = innerStroke + (visualConfig.tunnelWidth || 14);
      const color = visualConfig.lineColor || "#C48178";
      let d = `M 0 ${cy}`;
      if (trace.type === "straight") {
        d += ` L ${width} ${cy}`;
      } else if (trace.type === "zigzag") {
        const n = 8, step = width / n;
        for (let i = 0; i < n; i++) {
          const xMid = i * step + step / 2;
          const y = i % 2 === 0 ? cy - A : cy + A;
          d += ` L ${xMid} ${y} L ${(i + 1) * step} ${cy}`;
        }
      } else if (trace.type === "wave") {
        const n = 6, step = width / n;
        for (let i = 0; i < n; i++) {
          const x1 = i * step + step * 0.25, y1 = cy - A;
          const x2 = i * step + step * 0.75, y2 = cy + A;
          const x3 = (i + 1) * step, y3 = cy;
          d += ` C ${x1} ${y1}, ${x2} ${y2}, ${x3} ${y3}`;
        }
      } else if (trace.type === "arches") {
        const r = Math.min(40, width / 16);
        const period = 2 * r;
        const reps = Math.floor(width / period);
        for (let i = 0; i < reps; i++) {
          d += ` a ${r} ${r} 0 0 1 ${r} ${-r}`;
          d += ` a ${r} ${r} 0 0 1 ${r} ${r}`;
          d += ` a ${r} ${r} 0 0 1 ${r} ${r}`;
          d += ` a ${r} ${r} 0 0 1 ${r} ${-r}`;
        }
        d += ` L ${width} ${cy}`;
      } else if (trace.type === "loops") {
        const n = 7, step = width / n;
        for (let i = 0; i < n; i++) {
          const xL = i * step + step * 0.25;
          const xM = i * step + step * 0.5;
          const xR = i * step + step * 0.75;
          d += ` C ${xL} ${cy - A * 0.8}, ${xL} ${cy - A * 0.8}, ${xM} ${cy} C ${xR} ${cy + A * 0.8}, ${xR} ${cy + A * 0.8}, ${(i + 1) * step} ${cy}`;
        }
      } else if (trace.type === "stairs") {
        const n = 10, step = width / n;
        const up = cy - A * 0.7, down = cy + A * 0.2;
        let y = down;
        for (let i = 0; i < n; i++) {
          y = i % 2 === 0 ? up : down;
          d += ` L ${i * step} ${y} L ${(i + 1) * step} ${y}`;
        }
        d += ` L ${width} ${cy}`;
      } else if (trace.type === "mountains") {
        const n = 6, step = width / n;
        d += ` L 0 ${cy + A * 0.5}`;
        for (let i = 0; i < n; i++) {
          const apexX = i * step + step / 2;
          d += ` L ${apexX} ${cy - A} L ${(i + 1) * step} ${cy + A * 0.5} L ${(i + 1) * step} ${cy}`;
        }
        d += ` L ${width} ${cy}`;
      } else if (trace.type === "mix") {
        d += ` C ${width * 0.1} ${cy - A}, ${width * 0.18} ${cy + A}, ${width * 0.26} ${cy} c 20 -20, 40 -20, 60 0 C ${width * 0.42} ${cy + A}, ${width * 0.5} ${cy - A}, ${width * 0.58} ${cy} c 20 20, 40 20, 60 0 C ${width * 0.74} ${cy - A}, ${width * 0.82} ${cy + A}, ${width * 0.9} ${cy} L ${width} ${cy}`;
      }
      return `<svg width="${width}" height="${height}" style="display:block;"><path d="${d}" stroke="${color}" stroke-width="${outerStroke}" fill="none" stroke-linecap="round" stroke-linejoin="round"/><path d="${d}" stroke="#ffffff" stroke-width="${innerStroke}" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    },
    getTraceIconSize(question) {
      if (question && question.writing_config && question.writing_config.traces_visual) {
        return question.writing_config.traces_visual.iconSize || 40;
      }
      return 40;
    },
    getCaligraphyFontSize(question) {
      if (question && question.writing_config && question.writing_config.caligraphy) {
        return question.writing_config.caligraphy.fontSize || 36;
      }
      return 36;
    },
    getCaligraphyShowGuides(question) {
      if (question && question.writing_config && question.writing_config.caligraphy) {
        return question.writing_config.caligraphy.showGuides !== false;
      }
      return true;
    },
  },
};
</script>

<style>
/* Question display base styles */
.question-display-root .question {
  width: 100%;
}
.question-display-root .qd-circle {
  background: rgb(37, 137, 189);
  border-radius: 25px;
  color: white;
  height: 25px;
  font-weight: bold;
  width: 25px;
  display: table;
  margin: 0px 6px 0px 2px;
  text-align: center;
  padding: 1px;
}
.question-display-root .qd-circle p {
  vertical-align: middle;
  display: table-cell;
  font-size: 0.9em;
}

/* Alternatives */
.question-display-root .alternative {
  background-color: rgb(243, 243, 243);
  margin: 1em 0 1em 0;
  border-radius: 5px;
  padding: 1em;
  min-height: 3em;
  width: 60%;
}
.question-display-root .alt_correct {
  border-color: rgb(105, 226, 105);
  border-style: dashed;
  border-width: thin;
}
.question-display-root .alt_incorrect {
  border-color: rgb(236, 65, 65);
  border-style: dashed;
  border-width: thin;
}

/* Essay & Dictation */
.question-display-root .essay_question {
  background-color: rgb(243, 243, 243);
  border-radius: 5px;
  width: 80%;
  height: 80px;
  padding: 10px 10px 25px 10px;
}
.question-display-root .dictation_question {
  background-color: rgb(243, 243, 243);
  border-radius: 5px;
  height: 50px;
  padding: 10px 10px 25px 10px;
}

/* Options (kind 3) */
.question-display-root .options {
  margin: 0 2px 0 2px;
}
.question-display-root .correct-option {
  background-color: rgb(243, 243, 243);
  margin: 1em 0 1em 0;
  border-radius: 5px;
  padding: 1em;
  min-height: 3em;
  max-height: 1000px;
  border-color: rgb(105, 226, 105);
  border-style: dashed;
  border-width: thin;
}
.question-display-root .incorrect-option {
  background-color: rgb(243, 243, 243);
  margin: 1em 0 1em 0;
  border-radius: 5px;
  padding: 1em;
  min-height: 3em;
  max-height: 1000px;
  border-color: rgb(236, 65, 65);
  border-style: dashed;
  border-width: thin;
}

/* Puzzle (kind 5) */
.question-display-root .puzzle-question { background-color: #f8f9fa; border-radius: 8px; padding: 20px; margin: 10px 0; }
.question-display-root .puzzle-options-row { display: flex; flex-wrap: wrap; gap: 15px; justify-content: center; align-items: stretch; }
.question-display-root .puzzle-option-item { border-radius: 8px; padding: 15px; transition: all 0.3s ease; min-height: 80px; min-width: 120px; flex: 0 0 auto; display: flex; align-items: center; justify-content: center; }
.question-display-root .puzzle-option-content { text-align: center; width: 100%; }
.question-display-root .puzzle-text p { margin: 0; font-weight: 500; }
.question-display-root .puzzle-audio { margin: 5px 0; }
.question-display-root .puzzle-image { margin: 5px 0; }
.question-display-root .puzzle-image img { max-width: 80px; max-height: 80px; object-fit: contain; }
.question-display-root .puzzle-correct-option { border: 3px solid #28a745 !important; background-color: #d4edda !important; box-shadow: 0 0 10px rgba(40, 167, 69, 0.3) !important; }
.question-display-root .puzzle-incorrect-option { border: 3px solid #dc3545 !important; background-color: #f8d7da !important; box-shadow: 0 0 10px rgba(220, 53, 69, 0.3) !important; }

/* Grid (kind 6) */
.question-display-root .grid-preview { margin: 10px 0; display: grid; gap: 6px; }
.question-display-root .grid-preview-cell { background-color: #ffffff; border: 1px dashed #cccccc; border-radius: 6px; width: 60px; height: 60px; display: flex; align-items: center; justify-content: center; background-size: cover; background-position: center; position: relative; }
.question-display-root .grid-preview-cell span { font-weight: 700; color: rgba(0,0,0,0.85); text-shadow: 0 1px 2px rgba(255,255,255,0.7); background: rgba(255,255,255,0.55); border-radius: 10px; padding: 2px 6px; }

/* Matrix (kind 7) */
.question-display-root .matrix-question { background-color: #f8f9fa; border-radius: 8px; padding: 20px; margin: 10px 0; }
.question-display-root .matrix-preview { margin: 10px 0; display: grid; gap: 8px; }
.question-display-root .matrix-preview-cell { background-color: #ffffff; border: 2px solid #e9ecef; border-radius: 8px; min-height: 80px; display: flex; align-items: center; justify-content: center; position: relative; transition: all 0.2s ease; }
.question-display-root .matrix-preview-cell:hover { border-color: #00b9ae; background: #f9fbfb; }
.question-display-root .matrix-preview-cell.question-mark-cell { background: #ffb3d1; border-color: #ff69b4; }
.question-display-root .matrix-image-container { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }
.question-display-root .matrix-image-container img { max-width: 100%; max-height: 100%; object-fit: contain; }
.question-display-root .question-mark { font-size: 32px; font-weight: bold; color: #333; }
.question-display-root .matrix-placeholder { color: #6c757d; font-size: 12px; text-align: center; }
.question-display-root .answer-options-preview { display: flex; gap: 10px; margin-top: 15px; flex-wrap: wrap; }
.question-display-root .answer-option-preview { background-color: #ffffff; border: 2px solid #e9ecef; border-radius: 8px; width: 80px; height: 80px; display: flex; align-items: center; justify-content: center; position: relative; transition: all 0.2s ease; }
.question-display-root .answer-option-preview:hover { border-color: #00b9ae; background: #f9fbfb; }
.question-display-root .answer-option-preview.correct-answer { border-color: #28a745; background: #d4edda; }
.question-display-root .answer-image-container { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }
.question-display-root .answer-image-container img { max-width: 100%; max-height: 100%; object-fit: contain; }
.question-display-root .answer-placeholder { color: #6c757d; font-size: 12px; text-align: center; }
.question-display-root .answer-number { position: absolute; top: -8px; right: -8px; background: #007bff; color: white; border-radius: 50%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: bold; }

/* Search (kind 8 & 10) */
.question-display-root .search-question { background-color: #f8f9fa; border-radius: 8px; padding: 20px; margin: 10px 0; }
.question-display-root .search-instruction { margin-bottom: 15px; }
.question-display-root .search-instruction p { margin-bottom: 10px; font-size: 16px; }
.question-display-root .goal-symbols-display { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.question-display-root .goal-symbol { background-color: #28a745; color: white; padding: 5px 10px; border-radius: 15px; font-weight: bold; font-size: 16px; }
.question-display-root .search-grid-preview { margin: 15px 0; display: grid; gap: 2px; border: 2px solid #ccc; padding: 5px; background-color: #f9f9f9; justify-content: center; }
.question-display-root .search-grid-preview-cell { width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; background-color: white; border: 1px solid #ddd; font-size: 14px; font-weight: bold; }

/* Visual Key (kind 9) */
.question-display-root .vk-preview { background-color: #f8f9fa; border-radius: 8px; padding: 16px; margin: 10px 0; }
.question-display-root .vk-preview-keys-container { margin-bottom: 12px; padding: 10px; border-radius: 10px; background: linear-gradient(90deg, #dbeafe, #c7d2fe); border: 2px solid #6366f1; }
.question-display-root .vk-preview-keys-title { text-align: center; font-weight: 700; margin-bottom: 8px; }
.question-display-root .vk-preview-keys { display: grid; grid-template-columns: repeat(9, 1fr); gap: 6px; }
.question-display-root .vk-preview-key { background: #fff; border: 2px solid #2f3640; border-radius: 4px; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 64px; }
.question-display-root .vk-preview-key-number { font-weight: 800; color: #1f2937; }
.question-display-root .vk-preview-key-symbol { color: #6366f1; font-size: 20px; font-weight: 700; }
.question-display-root .vk-preview-grid-container { margin-top: 8px; padding: 10px; border-radius: 10px; background: #e8f7ef; border: 2px solid #10b981; }
.question-display-root .vk-preview-grid-title { text-align: center; font-weight: 700; margin-bottom: 8px; }
.question-display-root .vk-preview-grid { display: grid; gap: 8px; }
.question-display-root .vk-preview-cell { background: #fff; border: 2px solid #2f3640; border-radius: 4px; padding: 6px; }
.question-display-root .vk-preview-number { font-weight: 700; color: #111827; margin-bottom: 6px; }
.question-display-root .vk-preview-draw { height: 26px; border: 2px dashed #9ca3af; border-radius: 6px; background: #ffffff; }

/* Writing (kind 11) */
.question-display-root .writing-preview { background-color: #f8f9fa; border-radius: 8px; padding: 20px; margin: 10px 0; }
.question-display-root .writing-preview-title { font-size: 18px; font-weight: 700; color: #2c3e50; margin-bottom: 15px; text-align: center; }
.question-display-root .traces-preview { display: flex; flex-direction: column; gap: 20px; }
.question-display-root .trace-preview-box { border: 2px solid #e9ecef; border-radius: 8px; padding: 15px; background: #fff; }
.question-display-root .trace-preview-content { display: flex; align-items: center; gap: 15px; }
.question-display-root .trace-icon { flex-shrink: 0; display: flex; align-items: center; justify-content: center; min-width: 50px; min-height: 50px; }
.question-display-root .trace-svg-container { flex: 1; display: flex; justify-content: center; }
.question-display-root .trace-svg { max-width: 100%; height: auto; }
.question-display-root .caligraphy-preview { display: flex; flex-direction: column; gap: 25px; }
.question-display-root .caligraphy-exercise-preview { border: 2px solid #e9ecef; border-radius: 8px; padding: 15px; background: #fff; }
.question-display-root .exercise-header-preview { font-size: 16px; font-weight: 700; color: #3498db; margin-bottom: 15px; }
.question-display-root .exercise-steps-preview { display: flex; gap: 15px; margin-top: 15px; }
.question-display-root .step-box-preview { flex: 1; border: 2px solid #bdc3c7; border-radius: 8px; padding: 15px; min-height: 100px; position: relative; }
.question-display-root .step-label-preview { position: absolute; top: -12px; left: 10px; background: #fff; padding: 2px 8px; font-size: 11px; font-weight: 700; border-radius: 4px; }
.question-display-root .step-model-preview { background: #f8f9fa; }
.question-display-root .step-model-preview .step-label-preview { color: #27ae60; }
.question-display-root .step-trace-preview .step-label-preview { color: #f39c12; }
.question-display-root .step-practice-preview .step-label-preview { color: #e74c3c; }
.question-display-root .text-model-preview { font-family: 'Brush Script MT', cursive; text-align: center; padding-top: 20px; color: #2c3e50; }
.question-display-root .text-dotted-preview { font-family: 'Brush Script MT', cursive; text-align: center; padding-top: 20px; color: transparent; -webkit-text-stroke: 1px #999; }
.question-display-root .guide-line-preview { border-top: 1px dashed #ddd; margin: 0 10px; }
.question-display-root .practice-space-preview { background: #fff; border: 1px dashed #ccc; border-radius: 4px; margin: 10px 0; }

/* Reading Evaluation (kind 12) */
.question-display-root .reading-evaluation-display { margin-top: 20px; }
.question-display-root .reading-evaluation-table-container { margin-top: 20px; }
.question-display-root .reading-evaluation-table { width: 100%; border-collapse: collapse; background: white; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); }
.question-display-root .reading-evaluation-table thead { background: linear-gradient(90deg, #6b46c1 0%, #8b5cf6 50%, #a78bfa 100%); }
.question-display-root .reading-evaluation-table th { padding: 12px 8px; color: white; font-weight: 600; font-size: 14px; border: none; }
.question-display-root .reading-evaluation-table tbody tr { border-bottom: 1px solid #e5e7eb; }
.question-display-root .reading-evaluation-table tbody tr:last-child { border-bottom: none; }
.question-display-root .reading-evaluation-table td { padding: 12px 8px; font-size: 14px; color: #1f2937; }
.question-display-root .reading-evaluation-table .numeric-value { color: #2563eb; font-weight: 600; }
.question-display-root .reading-evaluation-table .phrase-text { color: #1f2937; max-width: 400px; word-wrap: break-word; }
.question-display-root .decodificacion-display { display: inline-block; width: 60px; padding: 6px 8px; border: 1px solid #d1d5db; border-radius: 6px; background: #f9fafb; text-align: center; font-size: 14px; font-weight: 600; color: #2563eb; }
</style>
